import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Col, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import TextInput from "../../common/textfield/TextInput";
import { Button } from "../../common/Button";
import * as Yup from "yup";
import { editEncounterIntakeQuestions } from "../api/Encounter";
import { failed, success } from "../../common/Toastify";
import { getQuestionnaireResponseById, editQuestionnaireResponse } from "../../api/Questionnaire";
import { useSelector } from "react-redux";
import { buildQuestionResponseFromQR } from "../../../utils/questionnaireResponseUtils";
import BookingPhoneInput from "../../Admin/Scheduling/booking/BookingPhoneInput";
import { isValidPhoneNumber, parseBookingPhone } from "../../Admin/Scheduling/booking/phoneNumber";

const parseCallerNumber = (str) => {
  if (!str || typeof str !== "string") return { number: "", ext: "" };
  const extMatch = str.trim().match(/\s+ext\s+(\d+)$/i);
  if (extMatch) {
    return {
      number: str.slice(0, extMatch.index).trim(),
      ext: extMatch[1],
    };
  }
  return { number: str.trim(), ext: "" };
};

const buildCallerNumber = (number, ext) => {
  const n = number?.trim() || "";
  const e = ext?.trim() || "";
  return e ? `${n} ext ${e}` : n;
};

const EditReturnPhoneModal = ({ data, questionnaireResponse: questionnaireResponseProp, onShow, onHide, onSuccess, dialogClassName = "custom-dialog", formattedPhone = false, onSubmitOverride }) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [questionnaireResponseLocal, setQuestionnaireResponseLocal] = useState(null);
  const [qrError, setQrError] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const user = useSelector((state) => state?.auth?.user);

  const qrReference = data?.intakeQuestions?.questionnaireResponseId?.reference;
  const questionnaireResponseId = qrReference?.split("/")?.[1] || null;

  const questionnaireResponse = questionnaireResponseProp ?? questionnaireResponseLocal;

  const raw = data?.intakeQuestions?.callerNumber || "";
  const parsed = parseBookingPhone(raw, data?.intakeQuestions?.callerNumberExt);
  const { number: currentNumber, ext: currentExt } = formattedPhone ? { number: parsed.phone, ext: parsed.ext } : parseCallerNumber(raw);

  const formik = useFormik({
    initialValues: {
      callerNumber: currentNumber,
      callerNumberExt: currentExt,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      callerNumber: formattedPhone ? Yup.string().required("Enter a return phone number.").test("phone", "Enter a valid phone number.", isValidPhoneNumber) : Yup.string().max(20, "Maximum length is 20 characters."),
      callerNumberExt: Yup.string()
        .max(4, "Extension must be 4 digits or less.")
        .matches(/^[0-9]*$/, "Extension must be digits only."),
    }),
    onSubmit: async (values) => {
      values.callerNumber = values.callerNumber?.trim();
      values.callerNumberExt = values.callerNumberExt?.trim();
      setBtnLoading(true);
      const callerNumberStr = buildCallerNumber(
        values.callerNumber,
        values.callerNumberExt
      );

      try {
        if (onSubmitOverride) {
          await onSubmitOverride({ phone: values.callerNumber, ext: values.callerNumberExt });
          onSuccess?.();
          onHide();
          return;
        }
        // 1) Update encounter intakeQuestions.callerNumber
        await editEncounterIntakeQuestions({
          encounterId: data?.id,
          payload: {
            intakeQuestions: { callerNumber: callerNumberStr, ...(formattedPhone ? { callerNumberExt: values.callerNumberExt } : {}) },
          },
        });

        // 2) If we have a QuestionnaireResponse, update TELEPHONE there as well
        if (questionnaireResponseId && questionnaireResponse) {
          const qr = questionnaireResponse;
          const questionResponse = buildQuestionResponseFromQR(qr, {
            newTelephone: callerNumberStr,
          });

          if (questionResponse) {
            await editQuestionnaireResponse(qr.id, {
              encounterId: qr.encounterId,
              questionnaire: qr.questionnaire,
              patientID: qr.source,
              questionResponse,
            });
          }
        }

        success("Return phone number updated successfully.");
        onSuccess?.(callerNumberStr);
        onHide();
      } catch (err) {
        failed(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message ||
            "Return phone number update was unsuccessful."
        );
      } finally {
        setBtnLoading(false);
      }
    },
  });

  // When not provided by parent, fetch QuestionnaireResponse so we can update TELEPHONE by tag
  useEffect(() => {
    if (questionnaireResponseProp != null || !questionnaireResponseId) return;

    setQrLoading(true);
    setQrError(null);

    getQuestionnaireResponseById(questionnaireResponseId)
      .then((res) => {
        const qr = res?.data?.data || res?.data || res;
        if (!qr || !Array.isArray(qr.item)) {
          setQrError("Unable to load Questionnaire Response.");
          return;
        }
        setQuestionnaireResponseLocal(qr);
      })
      .catch((err) => {
        setQrError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err.message ||
            "Failed to load Questionnaire Response."
        );
      })
      .finally(() => setQrLoading(false));
  }, [questionnaireResponseId, questionnaireResponseProp]);

  const handleExtKeyPress = (e) => {
    if (!/^[0-9]+$/.test(e.key)) e.preventDefault();
  };

  return (
    <Modal
      backdropClassName
      backdrop="static"
      show={onShow}
      onHide={onHide}
      size="md"
      aria-labelledby="edit-return-phone-modal-title"
      centered
      className={dialogClassName}
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="edit-return-phone-modal-title">
          Edit Return Phone Number
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form
          className="common-form border-fields"
          onSubmit={formik.handleSubmit}
        >
          <Row>
            <Col>
              {formattedPhone ? <BookingPhoneInput value={formik.values.callerNumber} name="callerNumber"
                onChange={(value) => formik.setFieldValue("callerNumber", value)} onBlur={formik.handleBlur}
                error={formik.touched.callerNumber ? formik.errors.callerNumber : undefined} disabled={btnLoading} /> : <TextInput
                keyField="callerNumber"
                label="Phone Number"
                type="phone"
                formik={formik}
                placeholder="(000) 000-0000"
                required={true}
              />}
            </Col>
            <Col>
              <TextInput
                keyField="callerNumberExt"
                label="Ext."
                type="text"
                formik={formik}
                placeholder="Ext."
                maxLength={4}
                {...(formattedPhone ? { onChange: (event) => formik.setFieldValue("callerNumberExt", event.target.value.replace(/\D/g, "")) } : {})}
                onKeyPress={handleExtKeyPress}
                required={false}
                hideRequired={true}
              />
            </Col>
          </Row>
          {qrLoading && (
            <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
              Loading questionnaire details...
            </div>
          )}
          {qrError && (
            <div className="mt-2 error-text" style={{ fontSize: 12 }}>
              {qrError} Only the encounter phone number will be updated.
            </div>
          )}
          <div className="btn-wrap">
            <Button onClick={onHide} variant="secondary" title="Cancel">
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Update
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default EditReturnPhoneModal;
